import { AnchorHTMLAttributes, DetailedHTMLProps } from 'react'

import { ISO_FORMAT } from '@hhgtech/hhg-components/constants'
import { LOCALE } from '@hhgtech/hhg-components/types'
import dayjs from 'dayjs'

import { assetImagePath } from 'interfaces/constants'
import { Article, ArticleApi, ReviewedUserApi } from 'interfaces/types'

export const AUTHOR_CONFIG = {
  medical: {
    intlKey: {
      // Reviewed by {expert_display_name} · {specialty} · {organisation} · Written by {author_name} · Updated {post_modified}
      long: 'articlePage.reviewed_by.expert.medical',
      // Reviewed by {expert_display_name}
      short: 'articlePage.reviewed_by.expert.medical.short',
    },
    indicator: `${assetImagePath}/reviewer-medical.svg`,
  },
  expert: {
    intlKey: {
      // Reviewed by {expert_display_name} · {specialty} · {organisation} · Written by {author_name}. Updated {post_modified}
      long: 'articlePage.reviewed_by.expert',
      // Reviewed by {expert_display_name}
      short: 'articlePage.reviewed_by.expert.short',
    },
    indicator: `${assetImagePath}/reviewer-expert.svg`,
  },
  written_by: {
    intlKey: {
      // Written by {expert_display_name} · {specialty} · {organisation} · Updated {post_modified}
      long: 'articlePage.written_by.author',
      // Written by {expert_display_name}
      short: 'articlePage.written_by.author.short',
    },
    indicator: `${assetImagePath}/reviewer-editor.svg`,
  },
  fact_checked_by: {
    intlKey: {
      // Fact Checked by {fact_check_display_name} · Written by {author_name} · Updated {post_modified}
      long: 'articlePage.fact_checked_by.author',
      // Fact Checked by {fact_check_display_name}
      short: 'articlePage.fact_checked_by.author.short',
    },
    indicator: `${assetImagePath}/reviewer-fact-check.svg`,
  },
}

type AuthorProps = {
  article: Article | ArticleApi
  locale?: LOCALE
  version?: 'long' | 'short'
}

export type AuthorFormatMessageOptionsKeys =
  | 'author_name'
  | 'expert_display_name'
  | 'fact_check_display_name'
  | 'specialty'
  | 'organisation'
  | 'post_modified'

export function getAuthorFormatOptionsProps(
  reviewedUser?: ReviewedUserApi | null,
): DetailedHTMLProps<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  HTMLAnchorElement
> {
  if (!reviewedUser) {
    return { children: '' }
  }
  if (reviewedUser?.profile_url && reviewedUser?.turn_off_profile) {
    return { children: reviewedUser?.display_name || reviewedUser?.name || '' }
  }
  return {
    href: reviewedUser.profile_url || '',
    children: reviewedUser.display_name || reviewedUser.name || '',
  }
}
export type AuthorFormatMessage = {
  intlKey: string
  indicator: string
  avatar: string
  avatar_alt: string
  avatar_permalink: string
  formatOptions: Partial<
    Record<
      AuthorFormatMessageOptionsKeys,
      DetailedHTMLProps<
        AnchorHTMLAttributes<HTMLAnchorElement>,
        HTMLAnchorElement
      >
    >
  >
}
export function getAuthorFromArticleApi({
  article,
  locale = LOCALE.Vietnam,
  version: v = 'long',
}: AuthorProps): AuthorFormatMessage {
  const { reviewed, post_modified: articlePostModified } = article || {}
  const { expert, author, fact_check, is_author } = reviewed || {}
  const {
    avatar: fcAvatar = '',
    avatar_thumbnail: fcAvatarThumb = '',
    display_name: fcDisplayName = '',
    name: fcName = '',
    profile_url: fcProfileUrl = '',
    turn_off_profile: fcTurnOffProfile,
  } = fact_check || {}
  const modifiedDate = dayjs(articlePostModified)

  let authorConfig = AUTHOR_CONFIG.fact_checked_by
  let avatar = fcAvatarThumb || fcAvatar
  let avatar_alt = fcDisplayName || fcName
  let avatar_permalink = !fcTurnOffProfile ? fcProfileUrl : ''
  const post_modified = modifiedDate.isValid()
    ? modifiedDate.format(ISO_FORMAT[locale].dateFormat)
    : ''

  const formatOptions: AuthorFormatMessage['formatOptions'] = {
    expert_display_name: getAuthorFormatOptionsProps(expert),
    author_name: getAuthorFormatOptionsProps(author),
    fact_check_display_name: getAuthorFormatOptionsProps(fact_check),
    post_modified: { children: post_modified },
  }

  if (expert) {
    const {
      avatar: exAvatar = '',
      avatar_thumbnail: exAvatarThumb = '',
      display_name: exDisplayName = '',
      name: exName = '',
      profile_url: exProfileUrl = '',
      specialty = '',
      organisation = '',
      turn_off_profile: exTurnOffProfile,
    } = expert || {}
    formatOptions.specialty = { children: specialty }
    formatOptions.organisation = { children: organisation }
    // Image Alt arcoding to Image Src
    avatar = exAvatarThumb || exAvatar || avatar
    avatar_alt = exDisplayName || exName || avatar_alt
    avatar_permalink = !exTurnOffProfile ? exProfileUrl || avatar_permalink : ''

    if (is_author) {
      authorConfig = AUTHOR_CONFIG.written_by
    } else {
      authorConfig = AUTHOR_CONFIG.expert
    }
  }

  return {
    intlKey: authorConfig.intlKey[v],
    indicator: authorConfig.indicator,
    avatar,
    avatar_alt,
    avatar_permalink,
    formatOptions,
  }
}
